import { createAsyncThunk } from '@reduxjs/toolkit';

import {
  createBatchPaymentRequest,
  createContractRequest,
  createPaymentRequest,
  deleteContractRequest,
  extractErrorMessage,
  getContractRequest,
  handleApiError,
  isApiErrorWithStatus,
  listContractsRequest,
  reversePaymentRequest,
  showApiError,
  updateContractRequest,
} from '~/services';
import type {
  ContractId,
  propsRequestMarkInstallmentPaid,
  propsRequestMarkInstallmentsPaid,
  propsRequestMarkInstallmentsPaidByContract,
} from './interfaces';
import {
  contractsFailure,
  contractsListSuccess,
  contractsRequest,
  contractsSavingDone,
  contractsSavingStart,
  removeContract as removeContractAction,
  updateInstallment,
  upsertContract,
} from './slices';
import { mapContract, mapContractCard, mapInstallment } from './mappers';

export const fetchContracts = createAsyncThunk(
  'contracts/fetch',
  async (_, { dispatch }) => {
    dispatch(contractsRequest());
    try {
      const { data } = await listContractsRequest({ fields: 'card', limit: 100 });
      const cards = data.items;

      const details = await Promise.all(
        cards.map((c) =>
          getContractRequest(c.id)
            .then((r) => r.data)
            .catch(() => null),
        ),
      );

      const contracts: ReturnType<typeof mapContractCard>[] = [];
      const installmentsByContractId: Record<
        string,
        ReturnType<typeof mapInstallment>[]
      > = {};

      for (let i = 0; i < cards.length; i += 1) {
        const detail = details[i];
        if (detail) {
          contracts.push(mapContract(detail));
          installmentsByContractId[detail.id] = (detail.installments ?? []).map(
            (inst) => mapInstallment(inst, detail.id),
          );
        } else {
          const card = cards[i];
          contracts.push(mapContractCard(card));
          installmentsByContractId[card.id] = card.nextInstallment
            ? [
                {
                  id: card.nextInstallment.id,
                  contractId: card.id,
                  index: card.nextInstallment.index,
                  dueDate: card.nextInstallment.dueDate,
                  amount: card.nextInstallment.amountCents,
                  status: 'next',
                },
              ]
            : [];
        }
      }

      dispatch(
        contractsListSuccess({
          contracts,
          installmentsByContractId,
          fetchedAt: new Date().toISOString(),
        }),
      );
      return true;
    } catch (error) {
      dispatch(contractsFailure());
      handleApiError(error, {
        title: 'Não foi possível carregar os contratos',
      });
      return false;
    }
  },
);

export const fetchContractDetails = createAsyncThunk(
  'contracts/fetch-details',
  async (id: ContractId, { dispatch }) => {
    try {
      const { data } = await getContractRequest(id);
      const contract = mapContract(data);
      const installments =
        data.installments?.map((i) => mapInstallment(i, id)) ?? [];
      dispatch(upsertContract({ contract, installments }));
      return contract;
    } catch (error) {
      handleApiError(error, { title: 'Não foi possível carregar o contrato' });
      return null;
    }
  },
);

export const markInstallmentPaid = createAsyncThunk(
  'contracts/mark-installment-paid',
  async (
    { contractId, installmentId, paidAt, amountCents }: propsRequestMarkInstallmentPaid,
    { dispatch, getState },
  ) => {
    dispatch(contractsSavingStart());
    try {
      const state = getState() as { contracts: { installmentsByContractId: Record<string, ReturnType<typeof mapInstallment>[]> } };
      const current = state.contracts.installmentsByContractId[contractId]?.find(
        (i) => i.id === installmentId,
      );
      const value = amountCents ?? current?.amount ?? 0;
      const { data } = await createPaymentRequest(contractId, installmentId, {
        amountCents: value,
        paidAt: paidAt ?? new Date().toISOString(),
        method: 'manual',
      });
      if (current) {
        dispatch(
          updateInstallment({
            contractId,
            installment: {
              ...current,
              status: 'paid',
              paidAt: data.paidAt,
              paymentId: data.id,
              paidAmount: data.amountCents,
            },
          }),
        );
      }
      await dispatch(fetchContractDetails(contractId));
      dispatch(contractsSavingDone());
      return true;
    } catch (error) {
      dispatch(contractsSavingDone());
      if (isApiErrorWithStatus(error, 409)) {
        showApiError(error, {
          title: 'Parcela já paga',
          fallbackDescription:
            'Essa parcela já foi paga em outra sessão. Atualize a lista.',
        });
        return false;
      }
      return handleApiError(error);
    }
  },
);

function buildBatchIdempotencyKey(
  contractId: string,
  installmentIds: string[],
): string {
  const seed = [...installmentIds].sort().join('|');
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) % 2147483647;
  }
  return `batch_${contractId}_${hash.toString(36)}`;
}

export const markInstallmentsPaid = createAsyncThunk(
  'contracts/mark-installments-paid',
  async (
    { contractId, installmentIds, paidAt }: propsRequestMarkInstallmentsPaid,
    { dispatch },
  ) => {
    if (installmentIds.length === 0) return false;
    dispatch(contractsSavingStart());
    try {
      await createBatchPaymentRequest(
        contractId,
        {
          installmentIds,
          paidAt: paidAt ?? new Date().toISOString(),
          method: 'manual',
        },
        buildBatchIdempotencyKey(contractId, installmentIds),
      );
      await dispatch(fetchContractDetails(contractId));
      dispatch(contractsSavingDone());
      return true;
    } catch (error) {
      dispatch(contractsSavingDone());
      if (isApiErrorWithStatus(error, 409)) {
        await dispatch(fetchContractDetails(contractId));
        showApiError(error, {
          title: 'Parcelas desatualizadas',
          fallbackDescription:
            'Alguma parcela selecionada já foi paga em outra sessão. Confira a lista e tente novamente.',
        });
        return false;
      }
      return handleApiError(error, {
        title: 'Não foi possível registrar os pagamentos',
      });
    }
  },
);

export const markInstallmentsPaidByContract = createAsyncThunk(
  'contracts/mark-installments-paid-by-contract',
  async (
    { groups, paidAt }: propsRequestMarkInstallmentsPaidByContract,
    { dispatch },
  ) => {
    const valid = groups.filter((g) => g.installmentIds.length > 0);
    if (valid.length === 0) return false;

    dispatch(contractsSavingStart());
    const when = paidAt ?? new Date().toISOString();

    const results = await Promise.allSettled(
      valid.map((group) =>
        createBatchPaymentRequest(
          group.contractId,
          { installmentIds: group.installmentIds, paidAt: when, method: 'manual' },
          buildBatchIdempotencyKey(group.contractId, group.installmentIds),
        ),
      ),
    );

    await Promise.all(
      valid.map((group) => dispatch(fetchContractDetails(group.contractId))),
    );
    dispatch(contractsSavingDone());

    const failedIndexes = results
      .map((r, i) => (r.status === 'rejected' ? i : -1))
      .filter((i) => i >= 0);
    if (failedIndexes.length === 0) return true;

    const firstError = (results[failedIndexes[0]] as PromiseRejectedResult)
      .reason;
    const names = failedIndexes
      .map((i) => valid[i].label ?? 'contrato')
      .join(', ');
    const settledCount = valid.length - failedIndexes.length;

    showApiError(firstError, {
      title:
        settledCount > 0
          ? 'Registrado em parte'
          : 'Não foi possível registrar',
      description:
        settledCount > 0
          ? `As parcelas de ${names} não foram registradas: ${extractErrorMessage(firstError)} As dos outros contratos foram registradas.`
          : `Nenhuma parcela foi registrada: ${extractErrorMessage(firstError)}`,
    });
    return false;
  },
);

export const reverseInstallmentPayment = createAsyncThunk(
  'contracts/reverse-payment',
  async (
    {
      contractId,
      installmentId,
      paymentId,
    }: { contractId: ContractId; installmentId: string; paymentId: string },
    { dispatch },
  ) => {
    dispatch(contractsSavingStart());
    try {
      await reversePaymentRequest(contractId, installmentId, paymentId);
      await dispatch(fetchContractDetails(contractId));
      dispatch(contractsSavingDone());
      return true;
    } catch (error) {
      dispatch(contractsSavingDone());
      return handleApiError(error, {
        title: 'Não foi possível desfazer o pagamento',
      });
    }
  },
);

export const removeContract = createAsyncThunk(
  'contracts/remove',
  async (id: ContractId, { dispatch }) => {
    try {
      await deleteContractRequest(id);
      dispatch(removeContractAction(id));
      return true;
    } catch (error) {
      return handleApiError(error, {
        title: 'Não foi possível excluir o contrato',
      });
    }
  },
);

export const updateContractFields = createAsyncThunk(
  'contracts/update',
  async (
    {
      id,
      body,
    }: {
      id: ContractId;
      body: {
        contractNumber?: string;
        guarantee?: string | null;
        cropDescription?: string | null;
      };
    },
    { dispatch },
  ) => {
    try {
      const { data } = await updateContractRequest(id, body);
      const contract = mapContract(data);
      const installments =
        data.installments?.map((i) => mapInstallment(i, id)) ?? [];
      dispatch(upsertContract({ contract, installments }));
      return true;
    } catch (error) {
      return handleApiError(error, {
        title: 'Não foi possível salvar as alterações',
      });
    }
  },
);

export const createContract = createAsyncThunk(
  'contracts/create',
  async (
    body: Parameters<typeof createContractRequest>[0],
    { dispatch },
  ) => {
    dispatch(contractsSavingStart());
    try {
      const { data } = await createContractRequest(body);
      const contract = mapContract(data);
      const installments =
        data.installments?.map((i) => mapInstallment(i, contract.id)) ?? [];
      dispatch(upsertContract({ contract, installments }));
      dispatch(contractsSavingDone());
      return contract;
    } catch (error) {
      dispatch(contractsSavingDone());
      handleApiError(error, { title: 'Não foi possível salvar o contrato' });
      return null;
    }
  },
);
